import { Card, ProgressBar, Badge } from '@/components/common'

interface ServiceUsageSummary {
  serviceName: string
  totalUsed: number
  totalLimit: number | null
  percentage: number
  periodEnd: string | null
}

interface UsageSummaryProps {
  usageData?: Array<{
    service_name: string
    metric_name: string
    count: number
    limit?: number
    percentage_used?: number
    period_end: string
  }>
}

export const UsageSummary: React.FC<UsageSummaryProps> = ({ usageData }) => {
  // Group usage by service and calculate totals
  const serviceSummaries: ServiceUsageSummary[] = usageData
    ? (() => {
        const groupedByService = new Map<string, ServiceUsageSummary>()

        usageData.forEach((item) => {
          const serviceName = item.service_name
          const used = item.count
          const limit = item.limit && item.limit !== -1 ? item.limit : null

          if (!groupedByService.has(serviceName)) {
            groupedByService.set(serviceName, {
              serviceName,
              totalUsed: 0,
              totalLimit: 0,
              percentage: 0,
              periodEnd: item.period_end,
            })
          }

          const summary = groupedByService.get(serviceName)!
          summary.totalUsed += used
          if (limit) {
            summary.totalLimit = (summary.totalLimit || 0) + limit
          } else {
            summary.totalLimit = null
          }
        })

        // Calculate percentages
        return Array.from(groupedByService.values()).map((summary) => {
          const percentage =
            summary.totalLimit !== null && summary.totalLimit > 0
              ? Math.min((summary.totalUsed / summary.totalLimit) * 100, 100)
              : 0
          return {
            ...summary,
            percentage,
          }
        })
      })()
    : []

  const getUsageColor = (percentage: number): 'primary' | 'success' | 'warning' | 'danger' => {
    if (percentage >= 90) return 'danger'
    if (percentage >= 75) return 'warning'
    if (percentage > 0) return 'success'
    return 'primary'
  }

  return (
    <Card>
      <h3 className="text-lg font-semibold mb-4 text-text-main-light dark:text-text-main-dark">
        Usage Summary
      </h3>
      <div className="space-y-4">
        {serviceSummaries.length === 0 ? (
          <p className="text-sm text-text-sub-light dark:text-text-sub-dark text-center py-4">
            No usage data available
          </p>
        ) : (
          serviceSummaries.map((service) => {
            const color = getUsageColor(service.percentage)

            return (
              <div key={service.serviceName}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-text-main-light dark:text-text-main-dark">
                    {service.serviceName}
                  </span>
                  <span className="text-sm text-text-sub-light dark:text-text-sub-dark">
                    {service.totalUsed.toLocaleString()}{' '}
                    {service.totalLimit !== null
                      ? `/ ${service.totalLimit.toLocaleString()}`
                      : '/ Unlimited'}
                  </span>
                </div>
                {service.totalLimit !== null ? (
                  <ProgressBar
                    value={service.totalUsed}
                    max={service.totalLimit}
                    color={color}
                    className="mb-2"
                  />
                ) : (
                  <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-2">
                    <div className="h-full bg-primary w-full rounded-full"></div>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  {service.periodEnd && (
                    <span className="text-xs text-text-sub-light dark:text-text-sub-dark">
                      Resets: {new Date(service.periodEnd).toLocaleDateString()}
                    </span>
                  )}
                  {service.totalLimit !== null && service.percentage >= 75 && (
                    <Badge
                      variant={service.percentage >= 90 ? 'danger' : 'warning'}
                      size="sm"
                    >
                      {service.percentage >= 90 ? 'Limit Reached' : 'Approaching Limit'}
                    </Badge>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    </Card>
  )
}

